import { Button, Callout, Card, CardHeader, Checkbox, Field, Textarea } from '@provenance/design';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  FileUp,
  Info,
  Paperclip,
  Send,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PublicApiError, publicRequest } from '../api';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * THE APPLICANT'S RESPONSE PAGE
 *
 * Where the secure link in a "we need more information" email lands, for both flows that
 * ask someone for something without rejecting them: a registration applicant (spec
 * §10.2) and an exporter whose certificate request needs clarification (§7.1).
 *
 * The person opening this page HAS NO ACCOUNT — a registration applicant does not have
 * one yet, and requiring them to create one to answer a question would be absurd. So the
 * token in the URL is the whole authorisation, which shapes everything:
 *
 *  IT DISCLOSES ONLY WHAT THEY ALREADY KNOW. Their own company name, and what was asked
 *  of them. Nothing about the chamber's other applicants, nothing about the decision.
 *
 *  A BAD TOKEN AND AN EXPIRED TOKEN READ DIFFERENTLY. "Never valid" and "expired" need
 *  different actions from the reader — one means check the link, the other means ask for
 *  a new one.
 *
 *  IT IS SINGLE USE. Once they respond, the link stops working, so it cannot be replayed
 *  to keep appending to an application after a decision.
 * ════════════════════════════════════════════════════════════════════════════
 */

const MAX_FILE_MB = 25;

interface Prompt {
  kind: 'REGISTRATION' | 'CERTIFICATE';
  /** What the response concerns, in the responder's own terms. */
  subject: string;
  chamberName: string;
  chamberLogoUrl: string | null;
  items: Array<{ code: string; label: string }>;
  freeText: string | null;
  respondBy: string | null;
  /** An exporter may have re-prepared the certificate itself; an applicant cannot. */
  acceptsReplacementDocument: boolean;
}

export function Respond() {
  const { token } = useParams<{ token: string }>();
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const replacementInput = useRef<HTMLInputElement>(null);

  const prompt = useQuery({
    queryKey: ['public-response', token],
    queryFn: () => publicRequest<Prompt>(`/api/v1/public/response/${token}`),
    enabled: Boolean(token),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: async () => {
      const attachments = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          contentBase64: await toBase64(file),
        })),
      );
      const replacement = replacementFile
        ? {
            filename: replacementFile.name,
            contentType: replacementFile.type || 'application/pdf',
            contentBase64: await toBase64(replacementFile),
          }
        : null;
      return publicRequest('/api/v1/public/response', {
        method: 'POST',
        body: {
          token,
          message: message.trim() || null,
          attachments,
          replacementDocument: replacement,
        },
      });
    },
    onSuccess: () => setSent(true),
    onError: (err) =>
      setError(
        err instanceof PublicApiError
          ? err.message
          : 'Could not send your response. Check your connection and try again.',
      ),
  });

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const tooBig = Array.from(incoming).filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig.length > 0) {
      setError(`${tooBig.map((f) => f.name).join(', ')} — each file must be under ${MAX_FILE_MB}MB.`);
      return;
    }
    setError(null);
    setFiles((prev) => [...prev, ...Array.from(incoming)].slice(0, 10));
  };

  if (prompt.isLoading) {
    return (
      <Shell>
        <p className="[font-size:14px] text-[var(--pv-muted)]">Checking your link…</p>
      </Shell>
    );
  }

  if (prompt.isError || (!prompt.isLoading && !prompt.data)) {
    const expired =
      prompt.error instanceof PublicApiError && prompt.error.code === 'registration_token_expired';
    return (
      <Shell>
        <Card className="!border-[var(--pv-warn-line)] !bg-[var(--pv-warn-bg)]">
          <div className="flex items-start gap-3">
            <AlertTriangle aria-hidden className="mt-0.5 size-6 shrink-0 text-[var(--pv-warn)]" />
            <div>
              <h1 className="[font-size:1.25rem] font-bold leading-tight text-[var(--pv-warn)]">
                {expired ? 'This link has expired' : 'This link is not valid'}
              </h1>
              <p className="mt-2 [font-size:14px] leading-relaxed text-[var(--pv-ink-soft)]">
                {expired
                  ? 'Links expire so they cannot be used indefinitely. Reply to the email you received, or contact the chamber, and they will send you a new one.'
                  : 'The link may have been truncated by your email client, or it may already have been used — these work once. Try copying the whole link from the email.'}
              </p>
            </div>
          </div>
        </Card>
      </Shell>
    );
  }

  if (!prompt.data) return null;

  if (sent) {
    return (
      <Shell>
        <Card className="!border-[var(--pv-ok-line)] !bg-[var(--pv-ok-bg)]">
          <div className="flex items-start gap-3">
            <CheckCircle2 aria-hidden className="mt-0.5 size-7 shrink-0 text-[var(--pv-ok)]" />
            <div>
              <h1 className="[font-size:1.4rem] font-bold leading-tight tracking-[-0.02em] text-[var(--pv-ok)]">
                Thank you — that is with {prompt.data.chamberName}
              </h1>
              <p className="mt-3 [font-size:14.5px] leading-relaxed text-[var(--pv-ink-soft)]">
                Your response has gone back to the chamber and{' '}
                {prompt.data.kind === 'CERTIFICATE'
                  ? 'your certificate request is in their review queue again'
                  : 'your application is in their queue again'}
                . They will email you when they have reviewed it.
              </p>
              <p className="mt-3 [font-size:13px] text-[var(--pv-muted)]">
                This link has now been used and will not work again.
              </p>
            </div>
          </div>
        </Card>
      </Shell>
    );
  }

  const data = prompt.data;
  const isCertificate = data.kind === 'CERTIFICATE';

  return (
    <Shell>
      <p className="pv-eyebrow">{data.chamberName}</p>
      <h1 className="pv-display mt-2">
        They need a little more.
      </h1>
      <p className="pv-lede mt-4">
        {data.chamberName} is reviewing{' '}
        {isCertificate ? 'your certificate request' : 'the registration for'}{' '}
        <strong>{data.subject}</strong> and has asked for the following.{' '}
        {isCertificate
          ? 'Your request has not been rejected — it is paused, waiting for you.'
          : 'Your application has not been rejected — it is waiting for you.'}
      </p>

      {data.respondBy && (
        <Callout tone="info" className="mt-5" icon={<Info className="size-4" />}>
          They have asked for a response by{' '}
          <strong>{new Date(data.respondBy).toLocaleDateString('en-AU')}</strong>.
        </Callout>
      )}

      {error && (
        <Callout tone="stop" className="mt-4" icon={<AlertTriangle className="size-4" />}>
          {error}
        </Callout>
      )}

      <Card className="mt-5">
        <CardHeader title="What they asked for" />
        {data.items.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {data.items.map((item) => (
              <li key={item.code} className="grid grid-cols-[auto_minmax(0,1fr)] gap-2.5">
                <span aria-hidden className="mt-1.5 size-1.5 rounded-full bg-[var(--pv-brand)]" />
                <span className="[font-size:14px] leading-relaxed">{item.label}</span>
              </li>
            ))}
          </ul>
        )}
        {data.freeText && (
          <p className="mt-3 border-l-2 border-[var(--pv-brand)] pl-3 [font-size:14px] leading-relaxed text-[var(--pv-ink-soft)]">
            {data.freeText}
          </p>
        )}
      </Card>

      <Card className="mt-4">
        <CardHeader title="Your response" />

        <Field
          label="Attach the documents"
          help={`PDFs, images or office documents. Up to 10 files, each under ${MAX_FILE_MB}MB.`}
        >
          <div>
            <input
              ref={fileInput}
              type="file"
              multiple
              className="sr-only"
              onChange={(e) => addFiles(e.target.files)}
              aria-label="Choose files to attach"
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                addFiles(e.dataTransfer.files);
              }}
              className="flex w-full flex-col items-center gap-2 rounded-[var(--pv-radius)] border border-dashed border-[var(--pv-line-strong)] bg-[var(--pv-card-alt)] px-4 py-8 text-center transition-colors hover:border-[var(--pv-brand)]"
            >
              <FileUp aria-hidden className="size-5 text-[var(--pv-muted)]" />
              <span className="[font-size:13.5px] font-medium">Choose files, or drop them here</span>
            </button>
          </div>
        </Field>

        {files.length > 0 && (
          <ul className="mt-3 flex list-none flex-col gap-1.5 p-0">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center gap-2.5 rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-white px-3 py-2"
              >
                <Paperclip aria-hidden className="size-3.5 shrink-0 text-[var(--pv-faint)]" />
                <span className="min-w-0 flex-1 truncate [font-size:13px]">{file.name}</span>
                <span className="[font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                  {Math.round(file.size / 1024)} KB
                </span>
                <button
                  type="button"
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                  aria-label={`Remove ${file.name}`}
                  className="rounded-[var(--pv-radius-xs)] p-1 text-[var(--pv-faint)] hover:text-[var(--pv-stop)]"
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {data.acceptsReplacementDocument && (
          <div className="mt-5 border-t border-[var(--pv-line)] pt-4">
            <Field
              label="Replacing the certificate itself?"
              help="Only if you have re-prepared the certificate document. The chamber keeps the original either way."
            >
              <div>
                <input
                  ref={replacementInput}
                  type="file"
                  accept="application/pdf"
                  className="sr-only"
                  onChange={(e) => setReplacementFile(e.target.files?.[0] ?? null)}
                  aria-label="Choose a replacement certificate PDF"
                />
                {replacementFile ? (
                  <div className="flex items-center gap-2.5 rounded-[var(--pv-radius-sm)] border border-[var(--pv-brand)] bg-[var(--pv-brand-soft)] px-3 py-2">
                    <Paperclip aria-hidden className="size-3.5 shrink-0" />
                    <span className="min-w-0 flex-1 truncate [font-size:13px] font-medium">
                      {replacementFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setReplacementFile(null)}
                      aria-label="Remove the replacement certificate"
                      className="rounded-[var(--pv-radius-xs)] p-1 text-[var(--pv-muted)] hover:text-[var(--pv-stop)]"
                    >
                      <X aria-hidden className="size-3.5" />
                    </button>
                  </div>
                ) : (
                  <Button variant="secondary" onClick={() => replacementInput.current?.click()}>
                    Choose a replacement PDF
                  </Button>
                )}
              </div>
            </Field>
          </div>
        )}

        <Field
          label="Anything you want to tell them"
          className="mt-4"
          help="Optional, but useful if a document needs explaining."
        >
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder={
              isCertificate
                ? 'The packing list was corrected — 720 cartons is right, the 700 on the earlier version was a typo.'
                : 'The certificate of incorporation is attached. Our trading name differs from the registered name, which is why the ABN lookup showed something different.'
            }
          />
        </Field>

        <div className="mt-4 border-t border-[var(--pv-line)] pt-4">
          <Checkbox
            checked={confirmed}
            onCheckedChange={(v) => setConfirmed(v === true)}
            label="The information and documents I have provided are true and complete."
            description="Certificates of origin are official trade documents, and the chamber relies on what you tell them."
          />
        </div>
      </Card>

      <Button
        variant="primary"
        size="lg"
        className="mt-4 w-full"
        disabled={
          !confirmed || (files.length === 0 && !replacementFile && message.trim().length === 0)
        }
        loading={submit.isPending}
        leadingIcon={<Send className="size-4" />}
        onClick={() => submit.mutate()}
      >
        Send my response
      </Button>

      <p className="mt-3 text-center [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
        This link works once. Keep the email in case you need to check what was asked.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[44rem] px-4 py-10 sm:py-16">
      <div className="mb-8 flex items-center gap-2.5">
        <span className="grid size-9 place-items-center rounded-[var(--pv-radius)] bg-[var(--pv-ink)] [font-size:15px] font-bold text-[var(--pv-leaf)]">
          P
        </span>
        <span className="[font-size:16px] font-bold tracking-[-0.02em]">Provenir</span>
      </div>
      {children}
    </div>
  );
}

/** Strips the `data:…;base64,` prefix the API does not want. */
async function toBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  // Chunked, because String.fromCharCode(...bytes) blows the call stack on a large file.
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
